import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EMPTY, map, of, switchMap, tap } from 'rxjs';
import { Option } from '@sapphire/result';
import { match } from 'ts-pattern';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { SurveyService } from '@ps/survey-data-access';
import { decideTestAction, TestAction } from '@ps/survey-utils';

import { SurveyCreationCoreActions } from '../core/survey-creation-core.actions';
import {
  selectIsSurveyLoaded,
  selectSurvey,
  selectSurveyId,
} from '../core/survey-creation-core.selectors';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from './survey-creation-launch.actions';
import {
  selectLaunchForm,
  selectTestEntryLink,
} from './survey-creation-launch.selectors';

/**
 * The URLs section's TEST button, end to end: decide whether what the user is
 * looking at is already live, persist it if not, then open the survey's test
 * entry link. Split from `SurveyCreationLaunchEffects` because none of that
 * shares anything with populating the launch form — see `core/README.md` on
 * splitting per effect rather than per file.
 */
@Injectable()
export class SurveyCreationTestEntryLinkEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _surveyService = inject(SurveyService);
  private readonly _toastService = inject(ToastService);
  private readonly _document = inject(DOCUMENT);

  // Always emits exactly one action: the decision itself is pure, and each
  // outcome is someone else's job below.
  public readonly requestTestEntryLink$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.requestTestEntryLink),
      concatLatestFrom(() => [
        this._store.select(selectSurvey),
        this._store.select(selectLaunchForm),
      ]),
      map(([, survey, launchForm]) =>
        decideTestAction({
          survey: Option.from(survey),
          form: launchForm.value,
        }).match({
          ok: (decision) => this._commandFor(decision),
          err: (error) => this._rejectTest(error),
        }),
      ),
    ),
  );

  // The patch regenerates the entry link and answers with it, so the next
  // unchanged click opens that one rather than the link from before the edit.
  //
  // Gated on `selectIsSurveyLoaded`, not just `selectSurveyId`: the id is set
  // as soon as `init` runs, before `loadSurveyForEditSuccess` populates the
  // survey itself. A patch fired in that window would succeed server-side
  // but land on a reducer with no survey to merge it into (see
  // `patchTestUrlsSuccess` in the core reducer) and silently vanish.
  public readonly patchTestUrls$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.patchTestUrls),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectIsSurveyLoaded),
      ]),
      switchMap(([{ liveUrl, testUrl }, surveyId, isSurveyLoaded]) =>
        Option.from(surveyId).match({
          some: (id) =>
            isSurveyLoaded
              ? this._surveyService
                  .updateLiveTestUrls(id, { liveUrl, testUrl })
                  .pipe(
                    map((testEntryLink) =>
                      SurveyCreationLaunchApiActions.patchTestUrlsSuccess({
                        liveUrl,
                        testUrl,
                        testEntryLink,
                      }),
                    ),
                    this.psCatchError(
                      (error) =>
                        of(
                          SurveyCreationLaunchApiActions.testEntryLinkFailure({
                            error: error.message,
                          }),
                        ),
                      {
                        notify: true,
                        customMessage: `Error(s) while updating url(s)`,
                      },
                    ),
                  )
              : of(
                  this._rejectTest(
                    'The survey is still loading. Please try testing again in a moment.',
                  ),
                ),
          none: () => EMPTY,
        }),
      ),
    ),
  );

  // The only place this feature opens a tab. Both triggers arrive after their
  // own reducer has run, so the link read here is the current one.
  public readonly openTestEntryLink$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          SurveyCreationLaunchApiActions.testEntryLinkReady,
          SurveyCreationLaunchApiActions.patchTestUrlsSuccess,
        ),
        concatLatestFrom(() => this._store.select(selectTestEntryLink)),
        tap(([, link]) =>
          Option.from(link).inspect((entryLink) =>
            this._document.defaultView?.open(entryLink, '_blank', 'noopener'),
          ),
        ),
      ),
    { dispatch: false },
  );

  private _commandFor(decision: TestAction): Action {
    return match(decision)
      .with({ kind: 'open' }, () =>
        SurveyCreationLaunchApiActions.testEntryLinkReady(),
      )
      .with({ kind: 'save' }, () => SurveyCreationCoreActions.saveAndTest())
      .with({ kind: 'patch' }, ({ liveUrl, testUrl }) =>
        SurveyCreationLaunchActions.patchTestUrls({
          liveUrl,
          testUrl: Option.from(testUrl).filter(Boolean).unwrapOr(liveUrl),
        }),
      )
      .exhaustive();
  }

  // Nothing was sent, so there is no psCatchError hop to carry the toast —
  // same shape as the save pipeline reporting its form errors.
  private _rejectTest(error: string): Action {
    this._toastService.error(error);
    return SurveyCreationLaunchApiActions.testEntryLinkFailure({ error });
  }
}
